import Quill from 'quill';
import Fuse from 'fuse.js';
import type { ReferenceEntry } from '../types';

interface AutocompleteOptions {
  trigger: string;
  dataSource: () => Promise<ReferenceEntry[]>;
}

type FilteredReference = {
  ref: ReferenceEntry;
};

/**
 * QuillJS Autocomplete Module
 * Shows a dropdown with fuzzy-searchable reference tags when user types the trigger character
 */
class QuillAutocompleteModule {
  quill: Quill;
  options: AutocompleteOptions;
  container: HTMLElement | null = null;
  selectedIndex = 0;
  references: ReferenceEntry[] = [];
  filteredReferences: FilteredReference[] = [];
  fuse: Fuse<ReferenceEntry> | null = null;
  isOpen = false;
  triggerIndex = -1;
  searchTerm = '';
  renderedItems: Array<{
    item: HTMLElement;
    tagElement: HTMLElement;
    previewContainer: HTMLElement;
    previewInner: HTMLElement;
    startScroll: () => void;
    stopScroll: () => void;
  }> = [];

  constructor(quill: Quill, options: AutocompleteOptions) {
    this.quill = quill;
    this.options = options;

    this.quill.on('text-change', this.onTextChange.bind(this));
    this.quill.root.addEventListener('keydown', this.onKeyDown.bind(this));

    this.loadReferences();
  }

  async loadReferences() {
    try {
      this.references = await this.options.dataSource();

      this.fuse = new Fuse(this.references, {
        keys: ['tag'],
        threshold: 0.3,
        includeScore: true,
        includeMatches: true,
        minMatchCharLength: 1
      });
    } catch (error) {
      console.error('[QuillAutocomplete] Failed to load references:', error);
    }
  }

  onTextChange(_delta: any, _oldDelta: any, source: string) {
    if (source !== 'user') return;

    const selection = this.quill.getSelection();
    if (!selection) return;

    const textBeforeCursor = this.quill.getText(0, selection.index);
    const lastTriggerIndex = textBeforeCursor.lastIndexOf(this.options.trigger);

    if (lastTriggerIndex === -1) {
      this.hideAutocomplete();
      return;
    }

    const textAfterTrigger = textBeforeCursor.slice(lastTriggerIndex + this.options.trigger.length);

    if (textAfterTrigger.includes(' ') || textAfterTrigger.includes(']')) {
      this.hideAutocomplete();
      return;
    }

    this.triggerIndex = lastTriggerIndex;
    this.searchTerm = textAfterTrigger;
    this.filterReferences(this.searchTerm);
    this.showAutocomplete();
  }

  filterReferences(searchTerm: string) {
    const trimmed = searchTerm.trim();

    if (!this.fuse) {
      this.filteredReferences = this.references.slice(0, 10).map(ref => ({ ref }));
      return;
    }

    if (!trimmed) {
      this.filteredReferences = this.references.slice(0, 10).map(ref => ({ ref }));
    } else {
      const results = this.fuse.search(trimmed);
      this.filteredReferences = results.slice(0, 10).map(result => ({
        ref: result.item
      }));
    }

    // Only reset selectedIndex if dropdown is not open or selection is out of bounds
    if (!this.isOpen || this.selectedIndex >= this.filteredReferences.length) {
      this.selectedIndex = 0;
    }
  }

  showAutocomplete() {
    if (this.filteredReferences.length === 0) {
      this.hideAutocomplete();
      return;
    }

    if (!this.container) {
      this.createContainer();
    }

    const selection = this.quill.getSelection();
    if (selection) {
      const bounds = this.quill.getBounds(selection.index);
      if (bounds) {
        this.container!.style.position = 'absolute';
        this.container!.style.left = `${bounds.left}px`;
        this.container!.style.top = `${bounds.bottom + 6}px`;
        this.container!.style.zIndex = '3200';
      }
    }

    this.renderItems();
    this.isOpen = true;
  }

  createContainer() {
    this.container = document.createElement('div');
    this.container.className = 'quill-autocomplete-dropdown';
    this.container.style.cssText = `
      position: absolute;
      background: #ffffff;
      border: 1px solid rgba(148, 163, 184, 0.35);
      border-radius: 12px;
      box-shadow: 0 16px 32px rgba(15, 23, 42, 0.16);
      max-height: 280px;
      overflow-y: auto;
      min-width: 280px;
      z-index: 3200;
    `;

    const editorContainer = this.quill.root.parentElement;
    if (editorContainer) {
      if (getComputedStyle(editorContainer).position === 'static') {
        editorContainer.style.position = 'relative';
      }
      editorContainer.appendChild(this.container);
    }
  }

  renderItems() {
    if (!this.container) return;

    this.renderedItems.forEach(entry => entry.stopScroll());
    this.container.innerHTML = '';
    this.renderedItems = [];

    if (this.filteredReferences.length === 0) {
      const emptyItem = document.createElement('div');
      emptyItem.textContent = 'No references found';
      emptyItem.style.cssText = `
        padding: 12px 16px;
        color: #64748b;
        font-style: italic;
        text-align: center;
      `;
      this.container.appendChild(emptyItem);
      return;
    }

    this.filteredReferences.forEach((entry, index) => {
      const { ref } = entry;
      const item = document.createElement('div');
      item.className = 'autocomplete-item';
      item.dataset.index = index.toString();

      const tagElement = document.createElement('div');
      tagElement.innerHTML = this.highlightText(ref.tag);
      tagElement.style.cssText = `
        font-family: 'Courier New', monospace;
        font-weight: 600;
        font-size: 13px;
        margin-bottom: 2px;
        transition: color 0.15s;
      `;

      const previewContainer = document.createElement('div');
      previewContainer.style.cssText = `
        position: relative;
        font-size: 12px;
        line-height: 1.5;
        transition: color 0.15s;
        overflow: hidden;
        white-space: nowrap;
        mask-image: linear-gradient(to right, rgba(0,0,0,1) 70%, rgba(0,0,0,0));
      `;

      const previewInner = document.createElement('span');
      previewInner.innerHTML = this.highlightText(ref.reference.trim());
      previewInner.style.cssText = `
        display: inline-block;
        white-space: nowrap;
        padding-right: 32px;
      `;

      previewContainer.appendChild(previewInner);

      item.appendChild(tagElement);
      item.appendChild(previewContainer);

      item.style.cssText = `
        padding: 10px 16px;
        cursor: pointer;
        transition: all 0.15s ease;
        margin: 4px 6px;
        border-radius: 8px;
        border-left: 3px solid transparent;
      `;

      item.addEventListener('mouseenter', () => {
        this.selectedIndex = index;
        this.applySelectionStyles();
        startScroll();
      });

      item.addEventListener('mouseleave', () => {
        stopScroll();
      });

      // Prevent mousedown from blurring the Quill editor
      item.addEventListener('mousedown', (e) => {
        e.preventDefault();
      });

      item.addEventListener('click', () => {
        this.insertReference(ref.tag);
      });

      const { startScroll, stopScroll } = this.createScrollHandlers(previewContainer, previewInner);

      this.renderedItems.push({
        item,
        tagElement,
        previewContainer,
        previewInner,
        startScroll,
        stopScroll
      });

      this.container!.appendChild(item);
    });

    this.applySelectionStyles();
  }

  hideAutocomplete() {
    this.renderedItems.forEach(entry => entry.stopScroll());
    this.renderedItems = [];

    if (this.container && this.container.parentElement) {
      this.container.remove();
      this.container = null;
    }
    this.isOpen = false;
    this.triggerIndex = -1;
    this.searchTerm = '';
  }

  onKeyDown(e: KeyboardEvent) {
    if (!this.isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectNext();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.selectPrevious();
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        this.insertSelected();
        break;
      case 'Escape':
        e.preventDefault();
        this.hideAutocomplete();
        break;
    }
  }

  selectNext() {
    if (this.selectedIndex < this.filteredReferences.length - 1) {
      this.selectedIndex++;
      this.applySelectionStyles();
    }
  }

  selectPrevious() {
    if (this.selectedIndex > 0) {
      this.selectedIndex--;
      this.applySelectionStyles();
    }
  }

  insertSelected() {
    if (this.filteredReferences.length === 0) {
      this.hideAutocomplete();
      return;
    }

    const selectedRef = this.filteredReferences[this.selectedIndex];
    if (selectedRef) {
      this.insertReference(selectedRef.ref.tag);
    }
  }

  insertReference(tag: string) {
    if (this.triggerIndex === -1) return;

    const selection = this.quill.getSelection();
    if (!selection) return;

    const deleteLength = this.options.trigger.length + this.searchTerm.length;

    // Use 'silent' source to prevent unwanted newlines and formatting
    this.quill.deleteText(this.triggerIndex, deleteLength, 'silent');
    this.quill.insertText(this.triggerIndex, tag, 'silent');
    this.quill.setSelection(this.triggerIndex + tag.length, 0, 'user');

    this.hideAutocomplete();
  }

  private escapeHtml(text: string): string {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    };
    return text.replace(/[&<>"']/g, char => map[char]);
  }

  private escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\$&');
  }

  private highlightText(text: string): string {
    const term = this.searchTerm.trim();
    if (!term) {
      return this.escapeHtml(text);
    }

    const regex = new RegExp(this.escapeRegExp(term), 'ig');
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let result = '';

    while ((match = regex.exec(text)) !== null) {
      result += this.escapeHtml(text.slice(lastIndex, match.index));
      result += `<mark style="background-color: rgba(254, 240, 138, 0.7); color: inherit; padding: 0 0.12em; border-radius: 0.2em;">${this.escapeHtml(match[0])}</mark>`;
      lastIndex = match.index + match[0].length;
    }

    result += this.escapeHtml(text.slice(lastIndex));
    return result;
  }

  private applySelectionStyles() {
    if (!this.renderedItems.length) return;

    this.renderedItems.forEach(({ item, tagElement, previewContainer }) => {
      item.style.background = 'transparent';
      item.style.borderLeft = '3px solid transparent';
      tagElement.style.color = '#2563eb';
      previewContainer.style.color = '#475569';
    });

    const selected = this.renderedItems[this.selectedIndex];
    if (selected) {
      selected.item.style.background = 'rgba(59, 130, 246, 0.12)';
      selected.item.style.borderLeft = '3px solid #2563eb';
      selected.tagElement.style.color = '#1d4ed8';
      selected.previewContainer.style.color = '#1f2937';
      selected.item.scrollIntoView({ block: 'nearest' });
    }
  }

  private createScrollHandlers(previewContainer: HTMLElement, previewInner: HTMLElement) {
    let animation: Animation | null = null;

    const startScroll = () => {
      if (animation) return;
      const overflowAmount = previewInner.scrollWidth - previewContainer.clientWidth;
      if (overflowAmount <= 12) {
        return;
      }

      const duration = Math.min(20000, Math.max(4000, overflowAmount * 40));
      animation = previewInner.animate([
        { transform: 'translateX(0)' },
        { transform: `translateX(-${overflowAmount}px)` }
      ], {
        duration,
        easing: 'linear',
        fill: 'forwards'
      });
    };

    const stopScroll = () => {
      if (animation) {
        animation.cancel();
        animation = null;
      }
      previewInner.style.transform = 'translateX(0)';
    };

    return { startScroll, stopScroll };
  }
}

Quill.register('modules/autocomplete', QuillAutocompleteModule);

export default QuillAutocompleteModule;
